import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { TEAM_COLORS } from '../network/protocol';

// Interpolated unit state handed to the pool each frame by the Game.
export interface UnitFrame {
  id: number;
  kind: string;
  o: number;
  x: number;
  z: number;
  hpFrac: number;
  selected: boolean;
  moving: boolean;
  busy: boolean; // gathering / building / fighting
  carry: string; // resource on the head ('wood'|'gold'|'food' or '')
  carryFrac: number; // 0..1 load fullness
}

const MAXU = 420; // headroom for 7-player battles (~60 units/player)
const MAX_SWORDS = 210;
const MAX_BOWS = 140;
const MAX_SPEARS = 140;
const MAX_CARRY = 160; // per carried-good kind (villagers only)
const MAX_ELITE = 160; // rajput panoply sets (pauldrons + helms)
const MAXVGEAR = 160; // villager-only dressing: vests, turbans, turban bands

const ARM_LEN = 0.62;
const SHOULDER_Y = 1.28;
const HIP_Y = 0.62;

interface Rec {
  slot: number;
  yaw: number;
  px: number;
  pz: number;
  phase: number;
}

// Broad-bladed sword with a crossguard, origin at the grip so it extends
// from the fist. Chunky enough to read at gameplay zoom.
function swordGeometry(): THREE.BufferGeometry {
  const blade = new THREE.BoxGeometry(0.14, 0.9, 0.2);
  blade.translate(0, 0.47, 0);
  const guard = new THREE.BoxGeometry(0.3, 0.08, 0.26);
  guard.translate(0, 0.04, 0);
  return mergeGeometries([blade, guard]) ?? blade;
}

// Long pike with a leaf tip, origin at the grip so it rises from the fist.
function spearGeometry(): THREE.BufferGeometry {
  const shaft = new THREE.CylinderGeometry(0.05, 0.06, 1.9, 6);
  shaft.translate(0, 0.95, 0);
  const tip = new THREE.ConeGeometry(0.1, 0.34, 6);
  tip.translate(0, 2.05, 0);
  return mergeGeometries([shaft, tip]) ?? shaft;
}

// Rajput pauldrons: one pair, origin at the body center so a single
// instance caps both shoulders (yawed with the torso).
function pauldronGeometry(): THREE.BufferGeometry {
  const left = new THREE.BoxGeometry(0.34, 0.2, 0.36);
  left.translate(-0.42, 0, 0);
  const right = new THREE.BoxGeometry(0.34, 0.2, 0.36);
  right.translate(0.42, 0, 0);
  return mergeGeometries([left, right]) ?? left;
}

// Rajput helm: steel dome + red crest plume (two material groups).
function helmGeometry(): THREE.BufferGeometry {
  const dome = new THREE.SphereGeometry(0.27, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
  const plume = new THREE.ConeGeometry(0.09, 0.32, 6);
  plume.translate(0, 0.41, 0);
  return mergeGeometries([dome, plume], true) ?? dome;
}
// Paint every vertex of a geometry one flat color (for baked multi-part
// dressing merged into a single instanced mesh).
function paint(geo: THREE.BufferGeometry, color: THREE.Color): void {
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = color.r;
    arr[i * 3 + 1] = color.g;
    arr[i * 3 + 2] = color.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
}

// Pagri turban: squashed dome + darker wrap fold + gold jewel + crest,
// origin at the base so it sits on the head. Colors are baked vertex
// colors (one shared look for every player); the separate team band
// carries ownership.
function turbanGeometry(): THREE.BufferGeometry {
  const dome = new THREE.SphereGeometry(0.3, 10, 7, 0, Math.PI * 2, 0, Math.PI * 0.62);
  dome.scale(1, 0.78, 1);
  paint(dome, new THREE.Color(0xc96a1e));
  const wrap = new THREE.TorusGeometry(0.24, 0.09, 6, 12);
  wrap.rotateX(Math.PI / 2 - 0.25);
  wrap.translate(0, 0.02, 0);
  paint(wrap, new THREE.Color(0x9c4f14));
  const jewel = new THREE.OctahedronGeometry(0.06, 0);
  jewel.translate(0, 0.1, 0.3);
  paint(jewel, new THREE.Color(0xffd766));
  const crest = new THREE.ConeGeometry(0.1, 0.16, 6);
  crest.rotateZ(-0.4);
  crest.translate(0.12, 0.28, 0);
  paint(crest, new THREE.Color(0xe08a2d));
  // PolyhedronGeometry (jewel) is non-indexed while the rest are indexed —
  // normalize or the merge fails and villagers lose their turbans.
  const parts = [dome, wrap, jewel, crest].map((p) => (p.index ? p.toNonIndexed() : p));
  return mergeGeometries(parts) ?? dome;
}

// Brocade Nehru vest worn over the kurta: orange shell, gold button
// placket down the front, gold hem trim, shaded sides. Sized to swallow
// the torso box (0.62×0.72×0.38) with no z-fighting.
function vestGeometry(): THREE.BufferGeometry {
  // Segmented shell so the painted placket/trim land on real vertices
  // (a plain box only has corners — a center stripe would be impossible).
  const g = new THREE.BoxGeometry(0.68, 0.64, 0.44, 4, 4, 1);
  const pos = g.attributes.position as THREE.BufferAttribute;
  const nor = g.attributes.normal as THREE.BufferAttribute;
  const base = new THREE.Color(0xd97a1f);
  const shade = new THREE.Color(0xb85f14);
  const gold = new THREE.Color(0xf2c14e);
  const arr = new Float32Array(pos.count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const nz = nor.getZ(i);
    const x = pos.getX(i);
    const y = pos.getY(i);
    if (y < -0.24) {
      c.copy(gold); // hem trim all round
    } else if (nz > 0.5) {
      c.copy(base);
      if (Math.abs(x) < 0.055) c.copy(gold); // button placket
    } else {
      c.copy(shade);
    }
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

// Curved hunting bow: two limbs sweeping back from the grip (local -Z is
// behind the archer once yawed). Big enough to read at gameplay zoom.
function bowGeometry(): THREE.BufferGeometry {
  const limb = new THREE.CylinderGeometry(0.045, 0.06, 0.62, 5);
  limb.translate(0, 0.31, 0); // origin at the grip end
  const top = limb.clone();
  top.rotateX(-0.3);
  const bottom = limb.clone();
  bottom.rotateX(Math.PI + 0.3);
  const grip = new THREE.BoxGeometry(0.1, 0.2, 0.1);
  return mergeGeometries([top, bottom, grip]) ?? grip;
}

// Knocked arrow: light shaft along +Z, shown while the archer is fighting.
function knockedArrowGeometry(): THREE.BufferGeometry {
  const shaft = new THREE.CylinderGeometry(0.03, 0.03, 0.8, 5);
  shaft.rotateX(Math.PI / 2);
  const tip = new THREE.ConeGeometry(0.06, 0.14, 5);
  tip.rotateX(Math.PI / 2);
  tip.translate(0, 0, 0.45);
  return mergeGeometries([shaft, tip]) ?? shaft;
}

// UnitPool renders every unit as a small articulated humanoid — torso, head,
// two arms, two legs, plus pagri turbans + brocade vests for villagers and
// hand weapons for soldiers.
// Rajputs additionally wear steel pauldrons + crested helms. Everything is
// an InstancedMesh (16 unit meshes for 200+ units); limb slots recycle
// through freelists (object pooling).
//
// Walk cycle: legs/arms swing in opposition, phased per unit. Working units
// chop/pump the right arm. Units face their travel direction (yaw smoothed).
export class UnitPool {
  private torso: THREE.InstancedMesh;
  private heads: THREE.InstancedMesh;
  private turbans: THREE.InstancedMesh;
  private turbanBands: THREE.InstancedMesh;
  private vests: THREE.InstancedMesh;
  private arms: THREE.InstancedMesh;
  private legs: THREE.InstancedMesh;
  private swords: THREE.InstancedMesh;
  private spears: THREE.InstancedMesh;
  private pauldrons: THREE.InstancedMesh;
  private helms: THREE.InstancedMesh;
  private bows: THREE.InstancedMesh;
  private nocked: THREE.InstancedMesh;
  private carryWood: THREE.InstancedMesh;
  private carryGold: THREE.InstancedMesh;
  private carryFood: THREE.InstancedMesh;
  private rings: THREE.InstancedMesh;
  private bars: THREE.InstancedMesh;

  private slotOf = new Map<number, Rec>();
  private freeU: number[] = [];
  private swordOf = new Map<number, number>();
  private freeS: number[] = [];
  private spearOf = new Map<number, number>();
  private freeSP: number[] = [];
  private armorOf = new Map<number, number>();
  private freeAR: number[] = [];
  private helmOf = new Map<number, number>();
  private freeHE: number[] = [];
  private carryOf = new Map<number, { slot: number; kind: string }>();
  private freeCW: number[] = [];
  private freeCG: number[] = [];
  private freeCF: number[] = [];
  private bowOf = new Map<number, number>();
  private freeB: number[] = [];
  private turbanOf = new Map<number, number>();
  private freeTN: number[] = [];
  private bandOf = new Map<number, number>();
  private freeBD: number[] = [];
  private vestOf = new Map<number, number>();
  private freeV: number[] = [];

  private dummy = new THREE.Object3D();
  private tmpColor = new THREE.Color();
  private hand = new THREE.Vector3();
  private euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private time = 0;
  private legsColorDirty = false;
  // HP-bar colors per bar-index (bar slots reorder as units die/spawn, so
  // key by bar position, not body slot — avoids stale colors after a swap).
  private barColors: number[] = [];
  private barsColorDirty = false;

  constructor(scene: THREE.Scene) {
    const lam = (color: number) => new THREE.MeshLambertMaterial({ color });

    this.torso = new THREE.InstancedMesh(new THREE.BoxGeometry(0.62, 0.72, 0.38), lam(0xffffff), MAXU);
    this.heads = new THREE.InstancedMesh(new THREE.SphereGeometry(0.24, 8, 6), lam(0xe8b07d), MAXU);
    // Pagri turban (villagers): baked orange dome + wrap + jewel, origin at
    // the base so it sits on the head and yaws with the body.
    this.turbans = new THREE.InstancedMesh(
      turbanGeometry(),
      new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true }),
      MAXVGEAR,
    );
    // Turban band in team colors so ownership reads past the shared turban.
    const bandGeo = new THREE.TorusGeometry(0.29, 0.06, 6, 14);
    bandGeo.rotateX(Math.PI / 2);
    this.turbanBands = new THREE.InstancedMesh(bandGeo, lam(0xffffff), MAXVGEAR);
    // Brocade vest over the kurta (villagers): baked orange + gold placket.
    this.vests = new THREE.InstancedMesh(
      vestGeometry(),
      new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true }),
      MAXVGEAR,
    );
    // Limbs pivot at the joint: geometry origin moved to the top of the box.
    const armGeo = new THREE.BoxGeometry(0.18, ARM_LEN, 0.18);
    armGeo.translate(0, -ARM_LEN / 2, 0);
    this.arms = new THREE.InstancedMesh(armGeo, lam(0xffffff), MAXU * 2);
    // Dhoti legs + mojari shoes in one merged piece, tinted per kind below
    // (teal dhoti for villagers, brown for soldiers).
    const legGeo = new THREE.BoxGeometry(0.2, HIP_Y, 0.2);
    legGeo.translate(0, -HIP_Y / 2, 0);
    const shoeGeo = new THREE.BoxGeometry(0.22, 0.12, 0.32);
    shoeGeo.translate(0, -HIP_Y + 0.06, 0.05);
    const legsMerged = mergeGeometries([legGeo, shoeGeo]) ?? legGeo;
    this.legs = new THREE.InstancedMesh(legsMerged, lam(0xffffff), MAXU * 2);
    // Sword: origin at the hilt so it extends from the fist.
    this.swords = new THREE.InstancedMesh(swordGeometry(), lam(0xe8eef5), MAX_SWORDS);
    // Pike: origin at the grip so it rises from the fist.
    this.spears = new THREE.InstancedMesh(spearGeometry(), lam(0x7a5c38), MAX_SPEARS);
    // Rajput panoply: steel pauldrons + crested helm.
    this.pauldrons = new THREE.InstancedMesh(
      pauldronGeometry(),
      new THREE.MeshLambertMaterial({ color: 0x9fb0bd }),
      MAX_ELITE,
    );
    this.helms = new THREE.InstancedMesh(
      helmGeometry(),
      [
        new THREE.MeshLambertMaterial({ color: 0x9fb0bd }),
        new THREE.MeshLambertMaterial({ color: 0xc22f2f }),
      ],
      MAX_ELITE,
    );
    this.bows = new THREE.InstancedMesh(bowGeometry(), lam(0x6b4423), MAX_BOWS);
    this.nocked = new THREE.InstancedMesh(knockedArrowGeometry(), lam(0xe8dcc0), MAX_BOWS);
    // Hand-carried loads: log, gold chunk, food sack. Ridden in the left
    // fist (the right arm keeps chopping/swinging) so the trip home reads
    // at gameplay zoom.
    const logGeo = new THREE.CylinderGeometry(0.09, 0.09, 0.7, 6);
    this.carryWood = new THREE.InstancedMesh(logGeo, lam(0x9a6a35), MAX_CARRY);
    this.carryGold = new THREE.InstancedMesh(
      new THREE.OctahedronGeometry(0.18, 0),
      new THREE.MeshBasicMaterial({ color: 0xffe066 }),
      MAX_CARRY,
    );
    this.carryFood = new THREE.InstancedMesh(new THREE.SphereGeometry(0.2, 7, 6), lam(0xdd8850), MAX_CARRY);

    for (const m of [this.torso, this.heads, this.turbans, this.turbanBands, this.vests, this.arms, this.legs, this.swords, this.spears, this.pauldrons, this.helms, this.bows, this.nocked, this.carryWood, this.carryGold, this.carryFood]) {
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.frustumCulled = false;
      scene.add(m);
    }
    for (let i = 0; i < MAXU; i++) {
      this.torso.setColorAt(i, this.tmpColor.set('#888888'));
      this.arms.setColorAt(i * 2, this.tmpColor.set('#888888'));
      this.arms.setColorAt(i * 2 + 1, this.tmpColor.set('#888888'));
    }
    for (let i = 0; i < MAXVGEAR; i++) {
      this.turbanBands.setColorAt(i, this.tmpColor.set('#888888'));
    }
    this.resetFree();

    this.rings = new THREE.InstancedMesh(
      new THREE.RingGeometry(0.7, 0.95, 20),
      new THREE.MeshBasicMaterial({
        color: 0x4ade80,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
      MAXU,
    );
    this.rings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.rings.frustumCulled = false;
    scene.add(this.rings);

    this.bars = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1.1, 0.14),
      new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: false,
      }),
      MAXU,
    );
    this.bars.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.bars.frustumCulled = false;
    this.bars.renderOrder = 5;
    scene.add(this.bars);
    for (let i = 0; i < MAXU; i++) this.bars.setColorAt(i, this.tmpColor.set('#4ade80'));

    this.dummy.rotation.order = 'YXZ';
  }

  private resetFree(): void {
    this.freeU = [];
    this.freeS = [];
    this.freeSP = [];
    this.freeAR = [];
    this.freeHE = [];
    this.freeB = [];
    this.freeTN = [];
    this.freeBD = [];
    this.freeV = [];
    this.freeCW = [];
    this.freeCG = [];
    this.freeCF = [];
    for (let i = MAXU - 1; i >= 0; i--) this.freeU.push(i);
    for (let i = MAX_SWORDS - 1; i >= 0; i--) this.freeS.push(i);
    for (let i = MAX_SPEARS - 1; i >= 0; i--) this.freeSP.push(i);
    for (let i = MAX_ELITE - 1; i >= 0; i--) {
      this.freeAR.push(i);
      this.freeHE.push(i);
    }
    for (let i = MAX_BOWS - 1; i >= 0; i--) this.freeB.push(i);
    for (let i = MAXVGEAR - 1; i >= 0; i--) {
      this.freeTN.push(i);
      this.freeBD.push(i);
      this.freeV.push(i);
    }
    for (let i = MAX_CARRY - 1; i >= 0; i--) {
      this.freeCW.push(i);
      this.freeCG.push(i);
      this.freeCF.push(i);
    }
  }

  private hide(mesh: THREE.InstancedMesh, slot: number): void {
    this.dummy.position.set(0, -50, 0);
    this.dummy.rotation.set(0, 0, 0);
    this.dummy.scale.set(0.001, 0.001, 0.001);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot, this.dummy.matrix);
    this.dummy.scale.set(1, 1, 1);
  }

  // Head-load pool routing by resource kind.
  private carryMesh(kind: string): THREE.InstancedMesh {
    return kind === 'gold' ? this.carryGold : kind === 'food' ? this.carryFood : this.carryWood;
  }

  private carryFree(kind: string): number[] {
    return kind === 'gold' ? this.freeCG : kind === 'food' ? this.freeCF : this.freeCW;
  }

  sync(frames: UnitFrame[], camera: THREE.Camera, dt: number, opts?: { lowDetail?: boolean }): void {
    this.time += dt;
    const lowDetail = !!opts?.lowDetail;
    const seen = new Set<number>();
    const seenVillagerGear = new Set<number>();
    let ringN = 0;
    let barN = 0;
    // Dirty tracking: only re-upload GPU buffers for pools we touched.
    let bodyDirty = false;
    let turbanDirty = false;
    let bandDirty = false;
    let vestDirty = false;
    let swordDirty = false;
    let spearDirty = false;
    let armorDirty = false;
    let helmDirty = false;
    let bowDirty = false;
    let nockedDirty = false;
    let carryDirtyW = false;
    let carryDirtyG = false;
    let carryDirtyF = false;
    let torsoColorDirty = false;
    let armsColorDirty = false;
    let bandColorDirty = false;
    this.barsColorDirty = false;
    this.legsColorDirty = false;

    for (const f of frames) {
      let rec = this.slotOf.get(f.id);
      if (!rec) {
        if (this.freeU.length === 0) continue; // over cap: skip (rare)
        rec = { slot: this.freeU.pop()!, yaw: Math.random() * Math.PI * 2, px: f.x, pz: f.z, phase: Math.random() * 10 };
        this.slotOf.set(f.id, rec);
        const team = this.tmpColor.set(TEAM_COLORS[f.o] ?? '#fff');
        this.torso.setColorAt(rec.slot, team);
        this.arms.setColorAt(rec.slot * 2, team);
        this.arms.setColorAt(rec.slot * 2 + 1, team);
        torsoColorDirty = true;
        armsColorDirty = true;
        // Dhoti legs for villagers, plain brown for soldiers.
        const dhoti = this.tmpColor.set(f.kind === 'villager' ? '#14707a' : '#5b4632');
        this.legs.setColorAt(rec.slot * 2, dhoti);
        this.legs.setColorAt(rec.slot * 2 + 1, dhoti);
        this.legsColorDirty = true;
      }
      seen.add(f.id);
      bodyDirty = true;

      // Facing: derive travel direction from interpolated motion, smoothed.
      const dx = f.x - rec.px;
      const dz = f.z - rec.pz;
      const speed = dt > 0 ? Math.hypot(dx, dz) / dt : 0;
      if (speed > 0.4) rec.yaw = lerpAngle(rec.yaw, Math.atan2(dx, dz), Math.min(1, dt * 8));
      rec.px = f.x;
      rec.pz = f.z;

      const walking = f.moving && speed > 0.4;
      rec.phase += dt * (walking ? 9 : f.busy ? 7 : 2);
      const swing = walking ? Math.sin(rec.phase) * 0.6 : 0;
      const bob = walking ? Math.abs(Math.sin(rec.phase)) * 0.06 : Math.sin(this.time * 2 + f.id) * 0.012;

      // Facing basis on the ground plane.
      const fx = Math.sin(rec.yaw);
      const fz = Math.cos(rec.yaw);
      const rx = Math.cos(rec.yaw);
      const rz = -Math.sin(rec.yaw);

      // Torso (broader soldiers / slimmer archers / hulking rajputs).
      const build = f.kind === 'swordsman' ? 1.12 : f.kind === 'archer' ? 0.94 : f.kind === 'spearman' ? 1.05 : f.kind === 'rajput' ? 1.28 : 1;
      const tall = f.kind === 'rajput' ? 1.08 : 1;
      const deep = f.kind === 'rajput' ? 1.12 : 1;
      this.dummy.position.set(f.x, 0.98 + bob, f.z);
      this.dummy.rotation.set(0, rec.yaw, 0);
      this.dummy.scale.set(build, tall, deep);
      this.dummy.updateMatrix();
      this.torso.setMatrixAt(rec.slot, this.dummy.matrix);
      this.dummy.scale.set(1, 1, 1);

      // Head.
      this.dummy.position.set(f.x + fx * 0.04, 1.56 + bob, f.z + fz * 0.04);
      this.dummy.rotation.set(0, rec.yaw, 0);
      this.dummy.updateMatrix();
      this.heads.setMatrixAt(rec.slot, this.dummy.matrix);

      // Arms: swing in opposition to the legs; working units chop.
      let armL: number;
      let armR: number;
      if (walking) {
        armL = -swing;
        armR = swing;
      } else if (f.busy) {
        armL = -0.15;
        armR = -1.1 + Math.sin(rec.phase) * 0.5; // chop / slash pump
      } else {
        armL = Math.sin(this.time * 2 + f.id) * 0.04;
        armR = -armL;
      }
      const shY = SHOULDER_Y + bob;
      this.dummy.rotation.set(armL, rec.yaw, 0);
      this.dummy.position.set(f.x - rx * 0.4, shY, f.z - rz * 0.4);
      this.dummy.updateMatrix();
      this.arms.setMatrixAt(rec.slot * 2, this.dummy.matrix);
      this.dummy.rotation.set(armR, rec.yaw, 0);
      this.dummy.position.set(f.x + rx * 0.4, shY, f.z + rz * 0.4);
      this.dummy.updateMatrix();
      this.arms.setMatrixAt(rec.slot * 2 + 1, this.dummy.matrix);

      // Legs: opposition swing while walking, planted otherwise.
      const legL = walking ? swing : 0;
      const legR = walking ? -swing : 0;
      this.dummy.rotation.set(legL, rec.yaw, 0);
      this.dummy.position.set(f.x - rx * 0.16, HIP_Y, f.z - rz * 0.16);
      this.dummy.updateMatrix();
      this.legs.setMatrixAt(rec.slot * 2, this.dummy.matrix);
      this.dummy.rotation.set(legR, rec.yaw, 0);
      this.dummy.position.set(f.x + rx * 0.16, HIP_Y, f.z + rz * 0.16);
      this.dummy.updateMatrix();
      this.legs.setMatrixAt(rec.slot * 2 + 1, this.dummy.matrix);

      // Villager dressing: brocade vest over the kurta, pagri turban with
      // a team band — yaws and bobs with the body.
      if (f.kind === 'villager') {
        let vs = this.vestOf.get(f.id);
        if (vs === undefined && this.freeV.length > 0) {
          vs = this.freeV.pop()!;
          this.vestOf.set(f.id, vs);
        }
        if (vs !== undefined) {
          seenVillagerGear.add(f.id);
          this.dummy.position.set(f.x, 0.98 + bob, f.z);
          this.dummy.rotation.set(0, rec.yaw, 0);
          this.dummy.scale.set(build, tall, deep);
          this.dummy.updateMatrix();
          this.vests.setMatrixAt(vs, this.dummy.matrix);
          this.dummy.scale.set(1, 1, 1);
          vestDirty = true;
        }
        let tn = this.turbanOf.get(f.id);
        if (tn === undefined && this.freeTN.length > 0) {
          tn = this.freeTN.pop()!;
          this.turbanOf.set(f.id, tn);
        }
        if (tn !== undefined) {
          this.dummy.position.set(f.x + fx * 0.04, 1.78 + bob, f.z + fz * 0.04);
          this.dummy.rotation.set(0, rec.yaw, 0);
          this.dummy.updateMatrix();
          this.turbans.setMatrixAt(tn, this.dummy.matrix);
          turbanDirty = true;
        }
        let bd = this.bandOf.get(f.id);
        if (bd === undefined && this.freeBD.length > 0) {
          bd = this.freeBD.pop()!;
          this.bandOf.set(f.id, bd);
          this.turbanBands.setColorAt(bd, this.tmpColor.set(TEAM_COLORS[f.o] ?? '#fff'));
          bandColorDirty = true;
        }
        if (bd !== undefined) {
          this.dummy.position.set(f.x + fx * 0.04, 1.7 + bob, f.z + fz * 0.04);
          this.dummy.rotation.set(0, rec.yaw, 0);
          this.dummy.updateMatrix();
          this.turbanBands.setMatrixAt(bd, this.dummy.matrix);
          bandDirty = true;
        }
      }

      // Hand weapons follow the hand: hand = shoulder + R * (0,-ARM_LEN,0).
      // Rajputs share the sword pool (bigger frame, same blade).
      if (f.kind === 'swordsman' || f.kind === 'rajput') {
        let ws = this.swordOf.get(f.id);
        if (ws === undefined && this.freeS.length > 0) {
          ws = this.freeS.pop()!;
          this.swordOf.set(f.id, ws);
        }
        if (ws !== undefined) {
          this.euler.set(armR, rec.yaw, 0);
          this.hand.set(0, -ARM_LEN, 0).applyEuler(this.euler);
          this.dummy.position.set(f.x + rx * 0.4 + this.hand.x, shY + this.hand.y, f.z + rz * 0.4 + this.hand.z);
          // Blade canted outward from the body so it reads at range; the
          // chop swing then carves big slashes while fighting.
          this.dummy.rotation.set(armR, rec.yaw, -0.38);
          this.dummy.updateMatrix();
          this.swords.setMatrixAt(ws, this.dummy.matrix);
          swordDirty = true;
        }
      } else if (f.kind === 'spearman') {
        let ss = this.spearOf.get(f.id);
        if (ss === undefined && this.freeSP.length > 0) {
          ss = this.freeSP.pop()!;
          this.spearOf.set(f.id, ss);
        }
        if (ss !== undefined) {
          this.euler.set(armR, rec.yaw, 0);
          this.hand.set(0, -ARM_LEN, 0).applyEuler(this.euler);
          this.dummy.position.set(f.x + rx * 0.4 + this.hand.x, shY + this.hand.y, f.z + rz * 0.4 + this.hand.z);
          // Pike held near-upright; the chop swing becomes a thrust.
          this.dummy.rotation.set(armR, rec.yaw, -0.12);
          this.dummy.updateMatrix();
          this.spears.setMatrixAt(ss, this.dummy.matrix);
          spearDirty = true;
        }
      } else if (f.kind === 'archer') {
        let bs = this.bowOf.get(f.id);
        if (bs === undefined && this.freeB.length > 0) {
          bs = this.freeB.pop()!;
          this.bowOf.set(f.id, bs);
        }
        if (bs !== undefined) {
          // Bow carried upright at the left side, steadied while walking.
          this.dummy.position.set(f.x - rx * 0.45 + fx * 0.1, 0.95 + bob, f.z - rz * 0.45 + fz * 0.1);
          this.dummy.rotation.set(0.08 + armL * 0.25, rec.yaw, 0);
          this.dummy.updateMatrix();
          this.bows.setMatrixAt(bs, this.dummy.matrix);
          bowDirty = true;
          // Knocked arrow pointing at the enemy while fighting. Skipped on
          // low detail (sub-pixel at gameplay zoom) to save matrix writes.
          if (f.busy && !lowDetail) {
            this.dummy.position.set(f.x + fx * 0.35, 1.18 + bob, f.z + fz * 0.35);
            this.dummy.rotation.set(0, rec.yaw, 0);
            this.dummy.updateMatrix();
            this.nocked.setMatrixAt(bs, this.dummy.matrix);
            nockedDirty = true;
          } else if (!f.busy || lowDetail) {
            // Only hide when state flips; hide() every frame would dirty the
            // buffer for no visual change. We track via nockedDirty below —
            // hiding a parked instance repeatedly is still a write, so gate
            // on lowDetail transitions by writing once per frame only when
            // the archer is visible (cheap vs full buffer upload saved).
            this.hide(this.nocked, bs);
            nockedDirty = true;
          }
        }
      }

      // Rajput panoply: steel pauldrons ride the shoulders, crested helm
      // crowns the head — both yaw and bob with the body.
      if (f.kind === 'rajput') {
        let ar = this.armorOf.get(f.id);
        if (ar === undefined && this.freeAR.length > 0) {
          ar = this.freeAR.pop()!;
          this.armorOf.set(f.id, ar);
        }
        if (ar !== undefined) {
          this.dummy.position.set(f.x, SHOULDER_Y + 0.1 + bob, f.z);
          this.dummy.rotation.set(0, rec.yaw, 0);
          this.dummy.scale.set(1, 1, 1);
          this.dummy.updateMatrix();
          this.pauldrons.setMatrixAt(ar, this.dummy.matrix);
          armorDirty = true;
        }
        let he = this.helmOf.get(f.id);
        if (he === undefined && this.freeHE.length > 0) {
          he = this.freeHE.pop()!;
          this.helmOf.set(f.id, he);
        }
        if (he !== undefined) {
          this.dummy.position.set(f.x + fx * 0.04, 1.66 + bob, f.z + fz * 0.04);
          this.dummy.rotation.set(0, rec.yaw, 0);
          this.dummy.scale.set(1, 1, 1);
          this.dummy.updateMatrix();
          this.helms.setMatrixAt(he, this.dummy.matrix);
          helmDirty = true;
        }
      }

      // Hand-carry: villagers hold goods in the left fist on the trip home.
      // Bigger bundle as the load fills; swapped/freed as the trip ends.
      // Low-detail mode parks carry props (they pop back when quality
      // recovers) to save ~160 matrix writes/frame in big economies.
      const wantCarryRaw = f.kind === 'villager' ? f.carry : '';
      const wantCarry = lowDetail ? '' : wantCarryRaw;
      let cc = this.carryOf.get(f.id);
      if (!wantCarry || (wantCarry !== 'wood' && wantCarry !== 'gold' && wantCarry !== 'food')) {
        if (cc) {
          this.hide(this.carryMesh(cc.kind), cc.slot);
          if (cc.kind === 'wood') carryDirtyW = true;
          else if (cc.kind === 'gold') carryDirtyG = true;
          else carryDirtyF = true;
          this.carryFree(cc.kind).push(cc.slot);
          this.carryOf.delete(f.id);
          cc = undefined;
        }
      } else {
        if (cc && cc.kind !== wantCarry) {
          this.hide(this.carryMesh(cc.kind), cc.slot);
          if (cc.kind === 'wood') carryDirtyW = true;
          else if (cc.kind === 'gold') carryDirtyG = true;
          else carryDirtyF = true;
          this.carryFree(cc.kind).push(cc.slot);
          this.carryOf.delete(f.id);
          cc = undefined;
        }
        if (!cc) {
          const pool = this.carryFree(wantCarry);
          if (pool.length > 0) {
            cc = { slot: pool.pop()!, kind: wantCarry };
            this.carryOf.set(f.id, cc);
          }
        }
        if (cc) {
          // Left fist = left shoulder + R * (0,-ARM_LEN,0); the load rides
          // with the walk swing. Logs hang below the fist along the arm.
          this.euler.set(armL, rec.yaw, 0);
          this.hand.set(0, -ARM_LEN, 0).applyEuler(this.euler);
          const hx = f.x - rx * 0.4 + this.hand.x;
          const hy = shY + this.hand.y;
          const hz = f.z - rz * 0.4 + this.hand.z;
          const s = 0.55 + 0.45 * Math.min(Math.max(f.carryFrac, 0), 1);
          if (wantCarry === 'wood') {
            const inv = 1 / Math.max(Math.hypot(this.hand.x, this.hand.y, this.hand.z), 1e-6);
            this.dummy.position.set(hx + this.hand.x * inv * 0.3, hy + this.hand.y * inv * 0.3, hz + this.hand.z * inv * 0.3);
            this.dummy.rotation.set(armL, rec.yaw, 0);
          } else {
            this.dummy.position.set(hx, hy + 0.12, hz);
            this.dummy.rotation.set(0, rec.yaw, 0);
          }
          this.dummy.scale.set(s, wantCarry === 'food' ? s * 0.8 : s, s);
          this.dummy.updateMatrix();
          this.carryMesh(cc.kind).setMatrixAt(cc.slot, this.dummy.matrix);
          this.dummy.scale.set(1, 1, 1);
          if (cc.kind === 'wood') carryDirtyW = true;
          else if (cc.kind === 'gold') carryDirtyG = true;
          else carryDirtyF = true;
        }
      }

      if (f.selected && ringN < MAXU) {
        this.dummy.position.set(f.x, 0.07, f.z);
        this.dummy.rotation.set(-Math.PI / 2, 0, 0);
        this.dummy.updateMatrix();
        this.rings.setMatrixAt(ringN++, this.dummy.matrix);
        this.dummy.rotation.set(0, 0, 0);
      }
      if (barN < MAXU) {
        this.dummy.position.set(f.x, 2.0, f.z);
        this.dummy.quaternion.copy(camera.quaternion);
        this.dummy.scale.set(Math.max(f.hpFrac, 0.001), 1, 1);
        this.dummy.updateMatrix();
        this.bars.setMatrixAt(barN, this.dummy.matrix);
        this.dummy.quaternion.identity();
        // Only repaint the HP color when the health band flips — avoids a
        // full instanceColor re-upload every frame for hundreds of units.
        const bucket = f.hpFrac > 0.6 ? 0 : f.hpFrac > 0.3 ? 1 : 2;
        if (this.barColors[barN] !== bucket) {
          this.barColors[barN] = bucket;
          const c = bucket === 0 ? '#4ade80' : bucket === 1 ? '#fbbf24' : '#ef4444';
          this.bars.setColorAt(barN, this.tmpColor.set(c));
          this.barsColorDirty = true;
        }
        barN++;
      }
    }

    // Release slots of removed units back to the freelists.
    let releasedBody = false;
    for (const [id, rec] of this.slotOf) {
      if (!seen.has(id)) {
        this.hide(this.torso, rec.slot);
        this.hide(this.heads, rec.slot);
        this.hide(this.arms, rec.slot * 2);
        this.hide(this.arms, rec.slot * 2 + 1);
        this.hide(this.legs, rec.slot * 2);
        this.hide(this.legs, rec.slot * 2 + 1);
        this.freeU.push(rec.slot);
        this.slotOf.delete(id);
        releasedBody = true;
      }
      const ws = this.swordOf.get(id);
      if (ws !== undefined && !seen.has(id)) {
        this.hide(this.swords, ws);
        this.freeS.push(ws);
        this.swordOf.delete(id);
        swordDirty = true;
      }
      const ss = this.spearOf.get(id);
      if (ss !== undefined && !seen.has(id)) {
        this.hide(this.spears, ss);
        this.freeSP.push(ss);
        this.spearOf.delete(id);
        spearDirty = true;
      }
      const ar = this.armorOf.get(id);
      if (ar !== undefined && !seen.has(id)) {
        this.hide(this.pauldrons, ar);
        this.freeAR.push(ar);
        this.armorOf.delete(id);
        armorDirty = true;
      }
      const he = this.helmOf.get(id);
      if (he !== undefined && !seen.has(id)) {
        this.hide(this.helms, he);
        this.freeHE.push(he);
        this.helmOf.delete(id);
        helmDirty = true;
      }
      const bs = this.bowOf.get(id);
      if (bs !== undefined && !seen.has(id)) {
        this.hide(this.bows, bs);
        this.hide(this.nocked, bs);
        this.freeB.push(bs);
        this.bowOf.delete(id);
        bowDirty = true;
        nockedDirty = true;
      }
      const hs = this.turbanOf.get(id);
      if (hs !== undefined && !seenVillagerGear.has(id)) {
        this.hide(this.turbans, hs);
        this.freeTN.push(hs);
        this.turbanOf.delete(id);
        turbanDirty = true;
      }
      const bd = this.bandOf.get(id);
      if (bd !== undefined && !seenVillagerGear.has(id)) {
        this.hide(this.turbanBands, bd);
        this.freeBD.push(bd);
        this.bandOf.delete(id);
        bandDirty = true;
      }
      const vs = this.vestOf.get(id);
      if (vs !== undefined && !seenVillagerGear.has(id)) {
        this.hide(this.vests, vs);
        this.freeV.push(vs);
        this.vestOf.delete(id);
        vestDirty = true;
      }
      const cc = this.carryOf.get(id);
      if (cc !== undefined && !seen.has(id)) {
        this.hide(this.carryMesh(cc.kind), cc.slot);
        if (cc.kind === 'wood') carryDirtyW = true;
        else if (cc.kind === 'gold') carryDirtyG = true;
        else carryDirtyF = true;
        this.carryFree(cc.kind).push(cc.slot);
        this.carryOf.delete(id);
      }
    }
    // Upload only pools we touched — idle pools (e.g. no rajputs on the
    // field) cost zero GPU bandwidth.
    if (bodyDirty || releasedBody) {
      this.torso.instanceMatrix.needsUpdate = true;
      this.heads.instanceMatrix.needsUpdate = true;
      this.arms.instanceMatrix.needsUpdate = true;
      this.legs.instanceMatrix.needsUpdate = true;
    }
    if (turbanDirty) this.turbans.instanceMatrix.needsUpdate = true;
    if (bandDirty) this.turbanBands.instanceMatrix.needsUpdate = true;
    if (vestDirty) this.vests.instanceMatrix.needsUpdate = true;    if (swordDirty) this.swords.instanceMatrix.needsUpdate = true;
    if (spearDirty) this.spears.instanceMatrix.needsUpdate = true;
    if (armorDirty) this.pauldrons.instanceMatrix.needsUpdate = true;
    if (helmDirty) this.helms.instanceMatrix.needsUpdate = true;
    if (bowDirty) this.bows.instanceMatrix.needsUpdate = true;
    if (nockedDirty) this.nocked.instanceMatrix.needsUpdate = true;
    if (carryDirtyW) this.carryWood.instanceMatrix.needsUpdate = true;
    if (carryDirtyG) this.carryGold.instanceMatrix.needsUpdate = true;
    if (carryDirtyF) this.carryFood.instanceMatrix.needsUpdate = true;
    if (torsoColorDirty && this.torso.instanceColor) this.torso.instanceColor.needsUpdate = true;
    if (armsColorDirty && this.arms.instanceColor) this.arms.instanceColor.needsUpdate = true;
    if (this.legsColorDirty && this.legs.instanceColor) this.legs.instanceColor.needsUpdate = true;
    if (bandColorDirty && this.turbanBands.instanceColor) this.turbanBands.instanceColor.needsUpdate = true;
    this.rings.count = ringN;
    this.rings.instanceMatrix.needsUpdate = true;
    this.bars.count = barN;
    this.bars.instanceMatrix.needsUpdate = true;
    if (this.barsColorDirty && this.bars.instanceColor) this.bars.instanceColor.needsUpdate = true;
  }

  clear(): void {
    this.slotOf.clear();
    this.swordOf.clear();
    this.spearOf.clear();
    this.armorOf.clear();
    this.helmOf.clear();
    this.bowOf.clear();
    this.turbanOf.clear();
    this.bandOf.clear();
    this.vestOf.clear();
    this.carryOf.clear();
    this.barColors = [];
    this.resetFree();
    // Park every instance underground. Body counts stay at max, so merely
    // forgetting the slots would leave the last match's army frozen on the
    // new map. (Ring/bar counts are rewritten every sync frame.)
    const park = (m: THREE.InstancedMesh, n: number) => {
      for (let i = 0; i < n; i++) this.hide(m, i);
      m.instanceMatrix.needsUpdate = true;
    };
    park(this.torso, MAXU);
    park(this.heads, MAXU);
    park(this.turbans, MAXVGEAR);
    park(this.turbanBands, MAXVGEAR);
    park(this.vests, MAXVGEAR);
    park(this.arms, MAXU * 2);
    park(this.legs, MAXU * 2);
    park(this.swords, MAX_SWORDS);
    park(this.spears, MAX_SPEARS);
    park(this.pauldrons, MAX_ELITE);
    park(this.helms, MAX_ELITE);
    park(this.bows, MAX_BOWS);
    park(this.nocked, MAX_BOWS);
    park(this.carryWood, MAX_CARRY);
    park(this.carryGold, MAX_CARRY);
    park(this.carryFood, MAX_CARRY);
    park(this.rings, MAXU);
    park(this.bars, MAXU);
  }
}
